import {Injectable} from "@angular/core";
import {
  Auth,
  linkWithCredential,
  linkWithPopup,
  reauthenticateWithPopup,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  unlink,
  User
} from "@angular/fire/auth";
import {EmailAuthProvider, reauthenticateWithCredential, updateEmail, updatePassword} from "@firebase/auth";
import {filter, firstValueFrom, take} from "rxjs";
import {NotificationService} from "../shared/services/notification.service";
import {LoggerService} from "../shared/services/logger.service";
import {ParentWindowMessagingService} from "../shared/services/parent-window-messaging.service";
import {buildProvider, FirebaseErrorCode, mapFirebaseError} from "./firebase-auth-utils";
import {FIREBASE_AUTH_PROVIDERS, FirebaseAuthProviderId} from "./auth-providers";
import {EmailVerificationService} from "../shared/services/email-verification.service";
import jwtDecode from 'jwt-decode';
import {MatDialog} from '@angular/material/dialog';
import {
  PasswordReauthDialogComponent,
  PasswordReauthDialogData
} from './password-reauth-dialog/password-reauth-dialog.component';
import {
  SsoReauthConfirmationDialogComponent, SsoReauthConfirmationDialogData
} from './sso-reauth-confirmation-dialog/sso-reauth-confirmation-dialog.component';
import {
  ForceReloginDialogComponent,
  ForceReloginDialogData,
  ForceReloginReason,
  MSG_EMAIL_PASSWORD_ADDED,
} from './force-relogin-dialog/force-relogin-dialog.component';
import {AuthService} from '../shared/services/auth.service';
import {AccountService} from '../shared/services/account.service';
import {LinkedProvider} from '../shared/domain';
import {normalizeEmail} from '../shared/utils/email.utils';

export const MSG_VERIFY_NEW_EMAIL = $localize`:@@section.signInMethods.msg.verificationEmailSent:We have sent a verification email to your new address. Please verify it to continue using your account.`

const FRESH_SIGN_IN_WINDOW_MS = 2 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SignInMethodsFacade {
  private lastEnsuredFreshSignInAt: number | null = null;

  constructor(
    private readonly auth: Auth,
    private readonly authService: AuthService,
    private readonly accountService: AccountService,
    private readonly notifications: NotificationService,
    private readonly logger: LoggerService,
    private readonly parentWindowMessaging: ParentWindowMessagingService,
    private readonly emailVerificationService: EmailVerificationService,
    private readonly dialog: MatDialog,
  ) {
    // Start with clean Firebase state - sign out any existing session
    // Lazy authentication will only happen when explicitly needed via authenticateAndReauthenticate
    this.auth.signOut().catch((e) => {
      this.logger.warn('Failed to clear initial Firebase state', e);
    });
  }

  get userOrNull(): User | null {
    return this.auth.currentUser;
  }

  get user(): User {
    if (!this.auth.currentUser) {
      throw new Error('Firebase user not authenticated');
    }
    return this.auth.currentUser;
  }

  async refreshUser(): Promise<void> {
    await this.user.reload();
  }

  private async getSignInProviderFromToken(): Promise<string> {
    const idToken = await firstValueFrom(
      this.authService.authToken$.pipe(
        filter((token): token is string => token !== undefined),
        take(1)
      )
    );

    if (!idToken) {
      throw new Error('Authentication token not available');
    }

    const decoded: any = jwtDecode(idToken);
    const provider = decoded?.firebase?.sign_in_provider;

    if (!provider) {
      throw new Error('Unable to determine sign-in provider from token');
    }

    return provider;
  }

  /**
   * Selects a fallback provider from the list of linked providers.
   * Prioritizes PASSWORD provider, then returns the first available provider.
   *
   * @param linkedProviders - Array of currently linked providers
   * @returns Provider ID, or null if no providers are available
   */
  private selectFallbackProvider(linkedProviders: LinkedProvider[]): string | null {
    if (linkedProviders.length === 0) {
      return null;
    }

    // Prefer PASSWORD provider if available
    const passwordProvider = linkedProviders.find(
      p => p.providerId === FirebaseAuthProviderId.PASSWORD
    );
    if (passwordProvider) {
      return FirebaseAuthProviderId.PASSWORD;
    }

    // Otherwise, return the first available provider
    return linkedProviders[0].providerId;
  }

  /**
   * Ensures the user has a fresh Firebase authentication.
   * - Should be called BEFORE showing dialogs for sensitive operations.
   * - If last sign-in was within 2 minutes, skips reauthentication.
   *
   * @returns true if authenticated, false if cancelled or failed
   */
  async ensureFreshSignIn(): Promise<boolean> {
    try {
      // Get provider from token
      let provider = await this.getSignInProviderFromToken();

      // Fetch currently linked providers from backend to verify the provider is still linked
      const linkedProviders = await firstValueFrom(this.accountService.getLinkedProviders());
      const linkedProviderIds = new Set(linkedProviders.map(p => p.providerId.toString()));

      // If the token's provider is no longer linked, fall back to another provider
      if (!linkedProviderIds.has(provider)) {
        this.logger.info(
          `Provider ${provider} from token is no longer linked. Selecting fallback provider.`
        );

        const fallbackProvider = this.selectFallbackProvider(linkedProviders);
        if (!fallbackProvider) {
          throw new Error('No linked authentication providers available');
        }

        this.logger.info(`Using fallback provider: ${fallbackProvider}`);
        provider = fallbackProvider;
      }

      // If not authenticated with Firebase yet, authenticate first
      if (!this.auth.currentUser) {
        const result = await this.authenticateWithProvider(provider, linkedProviders);
        if (result) this.lastEnsuredFreshSignInAt = Date.now();
        return result;
      }

      // Reauth not needed when recently authenticated (within the fresh sign-in window)
      // Note: we track this ourselves because reauthenticateWithPopup does not update lastSignInTime
      if (this.lastEnsuredFreshSignInAt !== null &&
          Date.now() - this.lastEnsuredFreshSignInAt < FRESH_SIGN_IN_WINDOW_MS) {
        return true;
      }

      // Already authenticated but sign-in is old -> need to reauthenticate based on provider
      let result: boolean;
      if (provider === FirebaseAuthProviderId.PASSWORD) {
        result = await this.reauthenticateWithPassword();
      } else if (
        provider === FirebaseAuthProviderId.GOOGLE ||
        provider === FirebaseAuthProviderId.FACEBOOK ||
        provider === FirebaseAuthProviderId.APPLE
      ) {
        result = await this.reauthenticateWithSso(provider);
      } else {
        throw new Error(`Unsupported provider: ${provider}`);
      }
      if (result) this.lastEnsuredFreshSignInAt = Date.now();
      return result;
    } catch (e: any) {
      this.logger.error('Authentication failed', e);
      this.notifications.showErrorMessage(mapFirebaseError(e));
      return false;
    }
  }

  private async authenticateWithProvider(
    provider: string,
    linkedProviders: LinkedProvider[]
  ): Promise<boolean> {
    try {
      if (provider === FirebaseAuthProviderId.PASSWORD) {
        const passwordProvider = linkedProviders.find((p) => p.providerId === FirebaseAuthProviderId.PASSWORD)
        if (!passwordProvider?.email) throw new Error('User email not found');

        let errorMessage: string | undefined = undefined;

        while (true) {
          // Show password dialog
          const dialogRef = this.dialog.open(PasswordReauthDialogComponent, {
            width: '320px',
            disableClose: true,
            data: {
              email: passwordProvider.email ,
              errorMessage
            } as PasswordReauthDialogData
          })

          const result = await firstValueFrom(dialogRef.afterClosed())
          if (!result?.password) {
            return false
          }

          try {
            // Sign in with password
            await signInWithEmailAndPassword(this.auth, passwordProvider.email, result.password);
            return true;
          } catch (e: any) {
            if (e?.code === FirebaseErrorCode.WRONG_PASSWORD) {
              errorMessage = $localize`:@@common.validation.wrongPassword:The password you entered is incorrect. Please try again.`;
              continue;
            }
            throw e;
          }
        }

      } else {
        // For SSO, show confirmation and trigger popup
        const providerConfig = FIREBASE_AUTH_PROVIDERS.find(p => p.id === provider);
        const providerName = providerConfig?.name || 'SSO provider';

        const confirmDialogRef = this.dialog.open(SsoReauthConfirmationDialogComponent, {
          width: '320px',
          data: { providerName } as SsoReauthConfirmationDialogData
        });

        const confirmed = await firstValueFrom(confirmDialogRef.afterClosed());
        if (!confirmed) {
          return false;
        }

        const authProvider = buildProvider(provider);
        await signInWithPopup(this.auth, authProvider);

        // Verify that signed in user matches one of the linked providers
        const signedInProviderData = this.auth.currentUser?.providerData.find(p => p && p.providerId === provider);
        if (!signedInProviderData) {
          throw new Error(`Provider ${provider} not found after sign-in`);
        }

        // Check if this SSO account matches the user's linked provider by comparing emails
        const expectedProvider = linkedProviders.find(lp => lp.providerId === provider);
        if (expectedProvider?.email && signedInProviderData.email) {
          if (normalizeEmail(expectedProvider.email) !== normalizeEmail(signedInProviderData.email)) {
            await this.handleIdentityMismatch();
            return false;
          }
        }

        return true;
      }
    } catch (e: any) {
      if (e?.code === FirebaseErrorCode.POPUP_CLOSED_BY_USER || e?.code === FirebaseErrorCode.CANCELLED_POPUP_REQUEST) {
        this.notifications.showInfoMessage($localize`:@@section.signInMethods.msg.actionCanceled:The action was canceled.`);
        return false;
      }
      throw e;
    }
  }

  private async reauthenticateWithPassword(): Promise<boolean> {
    const expectedEmail = this.user.email;
    if (!expectedEmail) throw new Error('User email not found');

    let errorMessage: string | undefined;

    while (true) {
      const dialogRef = this.dialog.open(PasswordReauthDialogComponent, {
        width: '320px',
        disableClose: true,
        data: { email: expectedEmail, errorMessage }
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      if (!result?.password) {
        this.notifications.showInfoMessage($localize`:@@section.signInMethods.msg.actionCanceled:The action was canceled.`);
        return false;
      }

      try {
        const credential = EmailAuthProvider.credential(expectedEmail, result.password);
        await reauthenticateWithCredential(this.user, credential);

        // For password auth, verify email matches (should always match since credential uses same email)
        if (this.auth.currentUser?.email !== expectedEmail) {
          await this.handleIdentityMismatch();
          return false;
        }

        return true;
      } catch (e: any) {
        if (e?.code === FirebaseErrorCode.WRONG_PASSWORD) {
          errorMessage = $localize`:@@common.validation.wrongPassword:The password you entered is incorrect. Please try again.`;
          continue;
        }
        throw e;
      }
    }
  }

  private async reauthenticateWithSso(providerId: string): Promise<boolean> {
    try {
      const selectedProviderData = this.user.providerData.find(p => p && p.providerId === providerId);

      if (!selectedProviderData) {
        throw new Error(`Provider ${providerId} not found in user's provider data`);
      }

      const providerConfig = FIREBASE_AUTH_PROVIDERS.find(p => p.id === providerId);
      const providerName = providerConfig?.name || 'SSO provider';

      const confirmDialogRef = this.dialog.open(SsoReauthConfirmationDialogComponent, {
        width: '320px',
        data: { providerName } as SsoReauthConfirmationDialogData
      });

      const confirmed = await firstValueFrom(confirmDialogRef.afterClosed());
      if (!confirmed) {
        return false;
      }

      const provider = buildProvider(providerId);
      await reauthenticateWithPopup(this.user, provider);

      // Verify the provider UID matches (ensures same Google/Facebook/Apple account was used)
      const newProviderData = this.auth.currentUser?.providerData.find(p => p && p.providerId === providerId);
      if (!newProviderData || newProviderData.uid !== selectedProviderData.uid) {
        await this.handleIdentityMismatch();
        return false;
      }

      return true;
    } catch (e: any) {
      if (e?.code === FirebaseErrorCode.POPUP_CLOSED_BY_USER || e?.code === FirebaseErrorCode.CANCELLED_POPUP_REQUEST) {
        this.notifications.showInfoMessage($localize`:@@section.signInMethods.msg.actionCanceled:The action was canceled.`);
        return false;
      }
      throw e;
    }
  }

  private async handleIdentityMismatch(): Promise<void> {
    this.notifications.showErrorMessage(
      $localize`:@@section.signInMethods.msg.identityMismatch:Different account was used! Please verify again using the account you’re currently signed in with.`
    );
    await this.auth.signOut();
  }

  private async showForceReloginDialog(
    reason: ForceReloginReason,
    emailVerificationRequired: boolean,
    email: string
  ): Promise<void> {
    this.parentWindowMessaging.sendLogout();
    const dialogRef = this.dialog.open(ForceReloginDialogComponent, {
      width: '520px',
      disableClose: true,
      data: { reason, emailVerificationRequired, email } as ForceReloginDialogData,
    });
    await firstValueFrom(dialogRef.afterClosed());
  }

  async changeEmail(newEmail: string): Promise<boolean> {
    try {
      const reauthed = await this.ensureFreshSignIn()
      if (!reauthed) return false;

      await updateEmail(this.user, newEmail);
      await this.refreshUser();

      const needsVerification = !this.user.emailVerified;
      if (needsVerification) {
        await sendEmailVerification(this.user);
        this.emailVerificationService.setVerificationRequired(newEmail);
      } else {
        this.emailVerificationService.clearVerificationRequired();
      }

      await this.showForceReloginDialog('EMAIL_CHANGED', needsVerification, newEmail);
      // TODO remove this one once changed by HonzaP. in web planner
      this.parentWindowMessaging.notifyEmailChanged(newEmail);

      return true;
    } catch (e: any) {
      this.logger.error('Firebase: Changing email failed', e);
      this.notifications.showErrorMessage(mapFirebaseError(e));

      return false;
    }
  }

  async changePassword(newPassword: string): Promise<boolean> {
    try {
      await updatePassword(this.user, newPassword)
      await this.showForceReloginDialog('PASSWORD_CHANGED', false, this.user.email!!);

      return true
    } catch (e: any) {
      this.logger.error('Firebase: Changing password failed', e)
      this.notifications.showErrorMessage(mapFirebaseError(e));

      return false;
    }
  }

  async linkEmailPassword(email: string, password: string): Promise<boolean> {
    try {
      const reauthed = await this.ensureFreshSignIn()
      if (!reauthed) return false;

      const credential = EmailAuthProvider.credential(email, password);
      await linkWithCredential(this.user, credential);
      await this.refreshUser();

      const needsVerification = !this.user.emailVerified;
      if (needsVerification) {
        await sendEmailVerification(this.user);
        this.emailVerificationService.setVerificationRequired(email);
        await this.showForceReloginDialog('EMAIL_PASSWORD_ADDED', true, email);
      } else {
        this.emailVerificationService.clearVerificationRequired();
        this.notifications.showSuccessMessage(MSG_EMAIL_PASSWORD_ADDED);
      }

      // TODO remove once changed by HonzaP. in Web planner
      this.parentWindowMessaging.notifyEmailChanged(email);

      return true;
    } catch (e: any) {
      this.logger.error('Firebase: adding email/password failed', e);
      this.notifications.showErrorMessage(mapFirebaseError(e));

      return false;
    }
  }

  async linkSsoProvider(providerId: string): Promise<void> {
    try {
      // Authenticate first (lazy auth)
      const reauthed = await this.ensureFreshSignIn()
      if (!reauthed) return;

      const provider = buildProvider(providerId);
      await linkWithPopup(this.user, provider);
      await this.refreshUser();
      this.notifications.showSuccessMessage($localize`:@@section.signInMethods.msg.added:The log-in method has been added successfully.`);
    } catch (e: any) {
      this.logger.error("Firebase: Linking new provider failed", e);
      this.notifications.showErrorMessage(mapFirebaseError(e));
    }
  }

  async unlinkProvider(providerId: string): Promise<void> {
    try {
      const reauthed = await this.ensureFreshSignIn()
      if (!reauthed) return;

      await unlink(this.user, providerId);
      await this.refreshUser();
      this.notifications.showSuccessMessage($localize`:@@section.signInMethods.msg.removed:The log-in method has been removed successfully.`);
    } catch (e: any) {
      this.logger.error("Firebase: Unlinking provider failed", e);
      this.notifications.showErrorMessage(mapFirebaseError(e));
    }
  }
}
